import React, { useEffect, useMemo, useState } from 'react';
import { CancelIcon } from '../../assets/icons/icons';
import { supabase } from '../../supabaseClient';
import '../CoachAssessmentModal/style.css';

const IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_IMAGE_BUCKET || 'coach-images';
const SIGNED_URL_TTL = 60 * 60;

const isAbsoluteUrl = (value = '') => (
  value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')
);

const normalizeImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return isAbsoluteUrl(item) ? { url: item } : { path: item };
        }
        if (item && typeof item === 'object') {
          const direct = item.url || item.publicUrl || item.dataUrl || item.src || null;
          if (direct) return { url: direct };
          const path = item.path || item.storagePath || item.key || null;
          return path ? { path } : null;
        }
        return null;
      })
      .filter(Boolean);
  }
  return [];
};

const formatValue = (value) => {
  if (value == null || value === '') return '—';
  return String(value);
};

export default function CoachCheckinModal({ isOpen, onClose, memberName, checkinRow, loading = false, error = '' }) {
  const [imageUrls, setImageUrls] = useState([]);
  const [imageStatus, setImageStatus] = useState('idle');
  const [lightboxSrc, setLightboxSrc] = useState('');

  const imageRefs = useMemo(() => normalizeImages(checkinRow?.images), [checkinRow]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (lightboxSrc) {
        setLightboxSrc('');
        return;
      }
      onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, lightboxSrc, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setImageUrls([]);
      setImageStatus('idle');
      setLightboxSrc('');
    }
  }, [isOpen]);

  useEffect(() => {
    let active = true;
    const loadImages = async () => {
      if (!isOpen) return;
      if (!imageRefs.length) {
        if (active) setImageUrls([]);
        return;
      }
      if (active) setImageStatus('loading');
      const urls = await Promise.all(imageRefs.map(async (ref) => {
        if (ref.url) return ref.url;
        if (!ref.path) return null;
        try {
          const { data, error: signError } = await supabase.storage
            .from(IMAGE_BUCKET)
            .createSignedUrl(ref.path, SIGNED_URL_TTL);
          if (signError) return null;
          return data?.signedUrl || null;
        } catch {
          return null;
        }
      }));
      if (active) {
        setImageUrls(urls.filter(Boolean));
        setImageStatus('done');
      }
    };
    loadImages();
    return () => { active = false; };
  }, [imageRefs, isOpen]);

  if (!isOpen) return null;

  const submittedAt = checkinRow?.createdat ? new Date(checkinRow.createdat).toLocaleString() : null;
  const rows = [
    { q: 'Weight (lbs)', a: checkinRow?.weight },
    { q: 'Energy', a: checkinRow?.energy_rating },
    { q: 'Meal consistency', a: checkinRow?.meal_consistency },
    { q: 'Digestion', a: checkinRow?.digestion_rating },
    { q: 'Training', a: checkinRow?.training_rating },
    { q: 'Workout consistency', a: checkinRow?.workout_consistency },
    { q: 'Recovery', a: checkinRow?.recovery_rating },
    { q: 'Wellbeing', a: checkinRow?.wellbeing_rating },
    { q: 'Pain notes', a: checkinRow?.pain_notes },
    { q: 'Wins', a: checkinRow?.wins_summary },
  ];

  return (
    <div className="coach-modal-overlay" onClick={() => onClose?.()}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <div className="coach-modal-header">
          <div>
            <div className="coach-modal-title">Weekly Check-In</div>
            <div className="coach-muted">{memberName || 'Member'}</div>
            {submittedAt && <div className="coach-muted">{submittedAt}</div>}
          </div>
          <button type="button" className="coach-modal-close" onClick={() => onClose?.()} aria-label="Close">
            <CancelIcon className="coach-modal-close-icon" />
          </button>
        </div>

        <div className="coach-modal-body">
          {loading ? (
            <div className="coach-muted">Loading check-in...</div>
          ) : error ? (
            <div className="coach-alert error">{error}</div>
          ) : !checkinRow ? (
            <div className="coach-alert">Check-in details unavailable.</div>
          ) : (
            <>
              <div className="coach-qa">
                <div className="coach-qa-section">
                  <div className="coach-qa-title">Responses</div>
                  <div className="coach-qa-list">
                    {rows.map((row) => (
                      <div key={row.q} className="coach-qa-row">
                        <div className="coach-qa-q">{row.q}</div>
                        <div className="coach-qa-a">{formatValue(row.a)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="coach-qa-section" style={{ marginTop: 12 }}>
                <div className="coach-qa-title">Check-In Photos</div>
                {imageRefs.length === 0 ? (
                  <div className="coach-muted">No photos uploaded yet.</div>
                ) : imageStatus === 'loading' ? (
                  <div className="coach-muted">Loading photos...</div>
                ) : imageUrls.length === 0 ? (
                  <div className="coach-muted">Photos are unavailable.</div>
                ) : (
                  <div className="coach-image-grid">
                    {imageUrls.map((src, idx) => (
                      <img
                        key={`checkin-photo-${idx}`}
                        src={src}
                        alt={`Check-in photo ${idx + 1}`}
                        className="coach-image-thumb"
                        loading="lazy"
                        onClick={() => setLightboxSrc(src)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setLightboxSrc(src);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {lightboxSrc && (
        <div
          className="coach-image-lightbox"
          onClick={(e) => {
            e.stopPropagation();
            setLightboxSrc('');
          }}
        >
          <button
            type="button"
            className="coach-image-lightbox-close"
            aria-label="Close image"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxSrc('');
            }}
          >
            ×
          </button>
          <img src={lightboxSrc} alt="Full size check-in" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
